import type { PipingSystem } from '../types';
import { COMPONENT_PROPS_KEY, type ComponentParameterValues } from './componentColumns';

export const NO_GROUPING_VALUE_LABEL = '(no value)';

const STATUS_SEVERITY: Record<PipingSystem['status'], number> = {
  Ready: 0,
  NotValidated: 1,
  Warnings: 2,
  Errors: 3,
};

function findCaseInsensitiveKey(source: Record<string, unknown>, parameterName: string): string | undefined {
  if (Object.prototype.hasOwnProperty.call(source, parameterName)) {
    return parameterName;
  }
  const lowered = parameterName.toLowerCase();
  return Object.keys(source).find((key) => key.toLowerCase() === lowered);
}

// Group rows must show the same value the component column renders, so the
// derivation reads the same componentProps map the table uses and falls back
// to the flat per-system prop (e2e fixtures and older connector payloads only
// carry the flat form).
function getGroupingValue(system: PipingSystem, parameterName: string): string {
  const componentProps = (system.componentProps ?? system[COMPONENT_PROPS_KEY]) as
    | ComponentParameterValues
    | undefined;
  if (componentProps) {
    const key = findCaseInsensitiveKey(componentProps, parameterName);
    const values = key ? componentProps[key] : undefined;
    if (Array.isArray(values)) {
      const joined = values
        .map((value) => String(value ?? '').trim())
        .filter((value) => value.length > 0)
        .join(', ');
      if (joined) {
        return joined;
      }
    }
  }

  const flatKey = findCaseInsensitiveKey(system as Record<string, unknown>, parameterName);
  const flatValue = flatKey ? String(system[flatKey] ?? '').trim() : '';
  return flatValue;
}

/**
 * Display-layer grouping for the export table in Parameter mode: one row per
 * distinct parameter value. Export itself keeps using the flat system list —
 * these rows exist only so the page reflects the configured grouping.
 */
export function groupSystemsByParameterValue(
  systems: readonly PipingSystem[],
  parameterName: string,
): PipingSystem[] {
  const trimmedName = parameterName.trim();
  if (!trimmedName) {
    return [...systems];
  }

  const groups = new Map<string, PipingSystem[]>();
  systems.forEach((system) => {
    const value = getGroupingValue(system, trimmedName) || NO_GROUPING_VALUE_LABEL;
    const bucket = groups.get(value);
    if (bucket) {
      bucket.push(system);
    } else {
      groups.set(value, [system]);
    }
  });

  return Array.from(groups.entries())
    .sort(([left], [right]) => {
      if (left === NO_GROUPING_VALUE_LABEL) {
        return right === NO_GROUPING_VALUE_LABEL ? 0 : 1;
      }
      if (right === NO_GROUPING_VALUE_LABEL) {
        return -1;
      }
      return left.localeCompare(right);
    })
    .map(([value, members]) => {
      const elementIds = new Set<string>();
      let elementCount = 0;
      let warningCount = 0;
      let errorCount = 0;
      let status: PipingSystem['status'] = 'Ready';
      members.forEach((member) => {
        elementCount += member.elementCount;
        warningCount += member.warningCount;
        errorCount += member.errorCount;
        (member.elementIds ?? []).forEach((id) => elementIds.add(String(id)));
        if (STATUS_SEVERITY[member.status] > STATUS_SEVERITY[status]) {
          status = member.status;
        }
      });

      return {
        systemName: value,
        elementCount,
        status,
        warningCount,
        errorCount,
        elementIds: [...elementIds],
        sessionExportCount: 0,
        [trimmedName]: value === NO_GROUPING_VALUE_LABEL ? '' : value,
      } as PipingSystem;
    });
}

// The table's selectable rows in Parameter mode are group labels, but the
// export pipeline resolves real systems by name. This translates a selection
// of group labels back to the member systems; names that are not group labels
// fall through so flat system-name callers (export all) keep working.
export function filterSystemsByGroupValues(
  systems: readonly PipingSystem[],
  parameterName: string,
  requestedNames: ReadonlySet<string>,
): PipingSystem[] {
  const trimmedName = parameterName.trim();
  if (!trimmedName || requestedNames.size === 0) {
    return [];
  }

  return systems.filter((system) => requestedNames.has(
    (getGroupingValue(system, trimmedName) || NO_GROUPING_VALUE_LABEL).toLowerCase()
  ));
}
